/**
 * NMS Product Card Component
 *
 * Handles variant selection, price updates, and cart integration
 * for the minimal product card component.
 *
 * Built following NMS JavaScript standards - extends NMSBaseClass,
 * uses window.neemans namespace, and integrates with existing theme systems.
 */

class NMSProductCard extends window.NMSBaseClass {
  constructor(element) {
    super();

    // Core properties
    this.element = element;
    this.productId = element.dataset.productId;
    this.context = element.dataset.context || 'card';

    // Component data
    this.productData = null;
    this.selectedVariant = null;
    this.selectedOptions = {};
    this.hasSizePopup = false;
    this.hasColorData = false;
    this.colorProducts = null;

    // Phase 5: Performance optimizations
    this.cacheCleanupInterval = null;

    // Storefront API configuration
    this.storefrontAccessToken = element.dataset.storefrontAccessToken;
    this.storeDomain = element.dataset.storeDomain || window.location.hostname;
    // Use inherited cache from NMSBaseClass instead of creating new Map

    // Size popup instance (created when needed)
    this.sizePopup = null;

    // Initialize global namespace
    window.neemans = window.neemans || {};
    window.neemans.productCard = window.neemans.productCard || {};
    window.neemans.productCard[this.productId] = this;

    // Bind context for event handlers
    this.handleOptionChange = this.handleOptionChange.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.handleCartSuccess = this.handleCartSuccess.bind(this);
    this.handleCartError = this.handleCartError.bind(this);
    this.handleSizePopupOpen = this.handleSizePopupOpen.bind(this);
    this.handleSizePopupClose = this.handleSizePopupClose.bind(this);
    this.handleColorSwatchClick = this.handleColorSwatchClick.bind(this);
  }

  // Initialize component after DOM ready
  init() {
    // Don't call super.init() to avoid document-wide event binding
    // Instead, manually call base class methods with proper scope
    this.#setupRefs();
    this.#bindEvents();

    try {
      this.#parseProductData();
      this.#initializeSelectedVariant();

      // Phase 5: Start smart cache management
      this.#initSmartCacheManagement();

      // Initialize custom swatch carousel if present
      this.#initSwatchCarousel();
    } catch (error) {
      console.error(`[NMS] Product Card init failed:`, error);
      this.#handleInitError(error);
    }
  }

  // Cleanup when component is removed
  destroy() {
    super.destroy();

    // Clean up size popup if it exists
    if (this.sizePopup) {
      this.sizePopup.destroy();
      this.sizePopup = null;
    }

    // Phase 5: Cleanup cache management
    this.#stopCacheInterval();
    if (this._boundVisibilityHandler) {
      document.removeEventListener('visibilitychange', this._boundVisibilityHandler);
      this._boundVisibilityHandler = null;
    }

    if (window.neemans.productCard?.[this.productId]) {
      delete window.neemans.productCard[this.productId];
    }
  }

  /**
   * Parse product data (Phase 4: Global data optimization with fallback)
   * @private
   */
  #parseProductData() {
    // Phase 4: Try to use optimized global data first
    const globalData = window.neemans?.productCards?.[this.productId];

    if (globalData && globalData.variants && globalData.variants.length > 0) {
      // Use optimized global data
      this.productData = globalData;

      // Extract configuration
      this.hasSizePopup = this.productData.size_popup || false;
      this.hasColorData = this.productData.has_color_data || false;
      this.colorData = this.productData.color_data || null;

      return;
    }

    // Fallback: Use original JSON parsing for backward compatibility
    const dataScript = this.element.querySelector('[data-nms-product-data]');

    if (!dataScript) {
      throw new Error('Product data script not found');
    }

    try {
      this.productData = JSON.parse(dataScript.textContent);

      if (!this.productData || !this.productData.variants) {
        throw new Error('Invalid product data structure');
      }

      // Extract size popup setting
      this.hasSizePopup = this.productData.size_popup || false;

      // Extract color switching data
      this.hasColorData = this.productData.has_color_data || false;
      this.colorData = this.productData.color_data || null;
    } catch (error) {
      throw new Error(`Failed to parse product data: ${error.message}`);
    }
  }

  /**
   * Setup element references using data-ref attributes
   * @private
   */
  #setupRefs() {
    // Price elements
    this.refs.priceContainer = this.element.querySelector('[data-ref="priceContainer"]');
    this.refs.currentPrice = this.element.querySelector('[data-ref="currentPrice"]');
    this.refs.comparePrice = this.element.querySelector('[data-ref="comparePrice"]');
    this.refs.saleBadge = this.element.querySelector('[data-ref="saleBadge"]');

    // Form elements
    this.refs.variantId = this.element.querySelector('[data-ref="variantId"]');
    this.refs.addButton = this.element.querySelector('[data-ref="addButton"]');
    this.refs.addButtonText = this.element.querySelector('[data-ref="addButtonText"]');
    this.refs.addButtonLoading = this.element.querySelector('[data-ref="addButtonLoading"]');

    // Size popup specific elements
    // Note: this.hasSizePopup is not parsed yet, so query unconditionally
    this.refs.selectSizeButton = this.element.querySelector('[data-ref="selectSizeButton"]');

    // Product elements (always needed for color switching)
    this.refs.productImage = this.element.querySelector('[data-ref="productImage"]');
    this.refs.productTitle = this.element.querySelector('[data-ref="productTitle"]');
    this.refs.productLink = this.element.querySelector('[data-ref="productLink"]');

    // Color switching elements (only when color data available)
    if (this.hasColorData) {
      this.refs.colorSwatches = this.element.querySelector('[data-ref="colorSwatches"]');
      this.refs.colorList = this.element.querySelector('[data-ref="colorList"]');
    }

    // Variant selection elements
    this.refs.variantsContainer = this.element.querySelector('[data-ref="variantsContainer"]');
    this.refs.optionSelects = this.element.querySelectorAll('[data-ref^="optionSelect"]');

    // Dynamic option value refs
    for (let i = 1; i <= 3; i++) {
      const optionValue = this.element.querySelector(`[data-ref="optionValue${i}"]`);
      if (optionValue) {
        this.refs[`optionValue${i}`] = optionValue;
      }
    }
  }

  /**
   * Bind event handlers
   * @private
   */
  #bindEvents() {
    // Auto-bind data-nms-click events (scoped to this card only)
    const clickElements = this.element.querySelectorAll('[data-nms-click]');
    for (const element of clickElements) {
      const handlerPath = element.getAttribute('data-nms-click');
      const [methodName, ...args] = handlerPath.split('/').filter(Boolean);

      if (this[methodName] && typeof this[methodName] === 'function') {
        element.addEventListener('click', event => {
          this[methodName].call(this, event, ...args);
        });
      }
    }

    // Option change events (dropdowns)
    this.refs.optionSelects?.forEach(select => {
      select.addEventListener('change', this.handleOptionChange);
    });

    // Option change events (swatches)
    const swatchInputs = this.element.querySelectorAll('.nms-product-card__swatch-input');
    swatchInputs.forEach(input => {
      input.addEventListener('change', this.handleOptionChange);
    });

    // Option change events (size boxes)
    const sizeInputs = this.element.querySelectorAll('.nms-product-card__size-input');
    sizeInputs.forEach(input => {
      input.addEventListener('change', this.handleOptionChange);
    });

    // Form submission - now handled via data-nms-click="/handleAddToCart"

    // Size popup button click - now handled via data-nms-click="/handleSizePopupOpen"

    // Color switching events - now handled via data-nms-click with conflict resolution

    // Cart events (existing theme integration)
    document.addEventListener('variant:add', this.handleCartSuccess);
    document.addEventListener('cart:error', this.handleCartError);
  }

  /**
   * Initialize with first available variant (Phase 4: Optimized with precomputed data)
   * @private
   */
  #initializeSelectedVariant() {
    // Phase 4: Use precomputed first available variant for better performance
    const precomputedVariantId = window.neemans?.productCards?.[this.productId]?.firstAvailableVariant;

    if (precomputedVariantId) {
      // Find the variant by the precomputed ID
      this.selectedVariant = this.productData.variants.find(v => v.id === precomputedVariantId);
    }

    // Fallback: Use original logic if precomputed data not available or variant not found
    if (!this.selectedVariant) {
      this.selectedVariant = this.productData.variants.find(v => v.available) || this.productData.variants[0];
    }

    if (!this.selectedVariant) {
      console.warn(`[NMS] No variants found for product ${this.productId}`);
      return;
    }

    // Parse selected options from variant
    this.selectedOptions = {
      option1: this.selectedVariant.option1,
      option2: this.selectedVariant.option2,
      option3: this.selectedVariant.option3,
    };

    // Update UI
    this.#updatePrice();
    this.#updateButtonState();
  }

  /**
   * Phase 5: Preload product image for instant color switching
   * @param {Object} productData - Product data with image information
   * @private
   */
  #preloadProductImage(productData) {
    if (!productData?.featured_media?.preview_image?.url) {
      return;
    }

    const imageUrl = productData.featured_media.preview_image.url;

    // Check if image is already preloaded or in browser cache
    const preloadKey = `preloaded-image:${imageUrl}`;

    if (this.cache.has(preloadKey)) {
      return; // Already preloaded
    }

    // Create image preload element
    const img = new Image();

    img.onload = () => {
      // Mark as preloaded to avoid duplicate requests
      this.cache.set(preloadKey, true);
    };

    img.onerror = () => {
      // Silently handle preload failures
    };

    // Start preloading
    img.src = imageUrl;
  }

  /**
   * Phase 5: Initialize smart cache management with periodic cleanup
   * Pauses when tab is hidden to avoid background work
   * @private
   */
  #initSmartCacheManagement() {
    this.#startCacheInterval();

    // Pause/resume on tab visibility change
    this._boundVisibilityHandler = () => {
      if (document.hidden) {
        this.#stopCacheInterval();
      } else {
        this.#startCacheInterval();
      }
    };
    document.addEventListener('visibilitychange', this._boundVisibilityHandler);

    // Initial cleanup
    this.#cleanupExpiredCache();
  }

  #startCacheInterval() {
    if (this.cacheCleanupInterval) return;
    this.cacheCleanupInterval = setInterval(() => {
      this.#cleanupExpiredCache();
    }, 120000);
  }

  #stopCacheInterval() {
    if (this.cacheCleanupInterval) {
      clearInterval(this.cacheCleanupInterval);
      this.cacheCleanupInterval = null;
    }
  }

  /**
   * Phase 5: Clean up expired cache entries
   * @private
   */
  #cleanupExpiredCache() {
    const now = Date.now();
    const expiredKeys = [];

    // Find expired cache entries
    for (const [key, value] of this.cache.entries()) {
      if (typeof value === 'object' && value.timestamp) {
        // 5 minutes TTL for prefetched data
        const isExpired = now - value.timestamp > 300000;

        if (isExpired) {
          expiredKeys.push(key);
        }
      }
    }

    // Remove expired entries
    expiredKeys.forEach(key => {
      this.cache.delete(key);
    });
  }

  /**
   * Handle option selection changes
   * @param {Event} event - Change event from option selector
   */
  handleOptionChange(event) {
    const optionPosition = parseInt(event.target.dataset.optionPosition);
    const optionValue = event.target.value;

    // Update selected options
    this.selectedOptions[`option${optionPosition}`] = optionValue;

    // Find matching variant
    const matchingVariant = this.#findMatchingVariant(this.selectedOptions);

    if (matchingVariant) {
      this.selectedVariant = matchingVariant;
      this.#updateVariantUI();
    } else {
      console.warn(`[NMS] No matching variant found for options:`, this.selectedOptions);
      this.#handleNoMatchingVariant();
    }
  }

  /**
   * Find variant matching current option selections
   * @param {Object} options - Selected option values
   * @returns {Object|null} Matching variant or null
   * @private
   */
  #findMatchingVariant(options) {
    return this.productData.variants.find(variant => {
      return (
        (options.option1 === null || variant.option1 === options.option1) &&
        (options.option2 === null || variant.option2 === options.option2) &&
        (options.option3 === null || variant.option3 === options.option3)
      );
    });
  }

  /**
   * Update UI when variant changes
   * @private
   */
  #updateVariantUI() {
    this.#updatePrice();
    this.#updateButtonState();
    this.#updateVariantId();
    this.#updateOptionLabels();
    this.#updateSizeAvailability();
  }

  /**
   * Update price display
   * @private
   */
  #updatePrice() {
    if (!this.selectedVariant || !this.refs.currentPrice) {
      console.warn('[NMS] Cannot update price - missing variant or price ref');
      return;
    }

    // Format prices using Shopify's money format
    const currentPrice = this.#formatMoney(this.selectedVariant.price);
    this.refs.currentPrice.textContent = currentPrice;

    // Handle compare at price
    if (this.refs.comparePrice && this.selectedVariant.compare_at_price) {
      const comparePrice = this.#formatMoney(this.selectedVariant.compare_at_price);

      if (this.selectedVariant.compare_at_price > this.selectedVariant.price) {
        this.refs.comparePrice.textContent = comparePrice;
        this.refs.comparePrice.style.display = '';

        // Calculate and show sale badge with percentage
        if (this.refs.saleBadge) {
          const discountAmount = this.selectedVariant.compare_at_price - this.selectedVariant.price;
          const discountPercentage = Math.round((discountAmount / this.selectedVariant.compare_at_price) * 100);
          this.refs.saleBadge.textContent = `${discountPercentage}% Off`;
          this.refs.saleBadge.style.display = '';
        }
      } else {
        this.refs.comparePrice.style.display = 'none';

        // Hide sale badge
        if (this.refs.saleBadge) this.refs.saleBadge.style.display = 'none';
      }
    }
  }

  /**
   * Update add to cart button state based on availability
   * @private
   */
  #updateButtonState() {
    if (!this.refs.addButton || !this.selectedVariant) return;

    const isAvailable = this.selectedVariant.available;

    // Update button enabled/disabled state
    this.refs.addButton.disabled = !isAvailable;

    // Update button text - respect size popup setting
    if (this.refs.addButtonText) {
      let buttonText;

      if (!isAvailable) {
        buttonText = window.themeStrings?.soldOut || 'Sold Out';
      } else if (this.hasSizePopup && this.#hasSizeOption()) {
        buttonText = 'Select Size';
      } else {
        buttonText = window.themeStrings?.addToCart || 'Add to Cart';
      }

      this.refs.addButtonText.textContent = buttonText;
    }

    // Update button classes
    this.refs.addButton.classList.toggle('nms-product-card__add-button--unavailable', !isAvailable);
  }

  /**
   * Update hidden variant ID input
   * @private
   */
  #updateVariantId() {
    if (!this.refs.variantId || !this.selectedVariant) return;

    this.refs.variantId.value = this.selectedVariant.id;
  }

  /**
   * Update option value labels
   * @private
   */
  #updateOptionLabels() {
    Object.keys(this.selectedOptions).forEach((optionKey, index) => {
      const optionNumber = index + 1;
      const optionValueRef = this.refs[`optionValue${optionNumber}`];

      if (optionValueRef && this.selectedOptions[optionKey]) {
        optionValueRef.textContent = this.selectedOptions[optionKey];
      }
    });
  }

  /**
   * Update size box availability based on current selections
   * @private
   */
  #updateSizeAvailability() {
    const sizeInputs = this.element.querySelectorAll('.nms-product-card__size-input');

    sizeInputs.forEach(input => {
      const optionPosition = parseInt(input.dataset.optionPosition);
      const optionValue = input.dataset.optionValue;

      // Check if this size is available with current other option selections
      const testOptions = { ...this.selectedOptions };
      testOptions[`option${optionPosition}`] = optionValue;

      const hasMatchingVariant = this.productData.variants.some(variant => {
        const matches =
          (testOptions.option1 === null || variant.option1 === testOptions.option1) &&
          (testOptions.option2 === null || variant.option2 === testOptions.option2) &&
          (testOptions.option3 === null || variant.option3 === testOptions.option3);
        return matches && variant.available;
      });

      // Update UI based on availability
      const label = input.closest('.nms-product-card__size-label');
      const sizeBox = input.nextElementSibling;

      if (!hasMatchingVariant) {
        input.disabled = true;
        label?.classList.add('nms-product-card__size-label--disabled');
        sizeBox?.classList.add('nms-product-card__size-box--disabled');
        sizeBox?.setAttribute('title', `${optionValue} (Out of Stock)`);
      } else {
        input.disabled = false;
        label?.classList.remove('nms-product-card__size-label--disabled');
        sizeBox?.classList.remove('nms-product-card__size-box--disabled');
        sizeBox?.setAttribute('title', optionValue);
      }
    });
  }

  /**
   * Handle case where no matching variant is found
   * @private
   */
  #handleNoMatchingVariant() {
    // Disable add to cart button
    if (this.refs.addButton) {
      this.refs.addButton.disabled = true;
    }

    // Update button text to show unavailable
    if (this.refs.addButtonText) {
      this.refs.addButtonText.textContent = 'Unavailable';
    }
  }

  /**
   * Handle add to cart button click (auto-bound)
   * @param {Event} event - Click event from button
   */
  handleAddToCart(event) {
    // Don't prevent default - let the theme's form handling work
    // The theme's product-form element will handle the AJAX submission

    // Just add loading state and mark as submitting for our success/error handlers
    this.#setCartButtonLoading(true);
    this.isSubmitting = true;
  }

  /**
   * Handle successful cart addition
   * @param {Event} event - Cart success event
   */
  handleCartSuccess(event) {
    // Only handle success if this card submitted the form
    if (!this.isSubmitting) return;

    // Remove loading state
    this.#setCartButtonLoading(false);

    // Brief success state
    this.#setSuccessState(true);
    setTimeout(() => {
      this.#setSuccessState(false);
      this.isSubmitting = false; // Reset submission flag
    }, 1500);
  }

  /**
   * Handle cart addition error
   * @param {Event} event - Cart error event
   */
  handleCartError(event) {
    // Only handle error if this card submitted the form
    if (!this.isSubmitting) return;

    // Remove loading state
    this.#setCartButtonLoading(false);

    // Show error state
    this.#setErrorState(true);
    setTimeout(() => {
      this.#setErrorState(false);
      this.isSubmitting = false; // Reset submission flag
    }, 3000);
  }

  // Loading state now handled by inherited showLoading/hideLoading methods

  /**
   * Set success state on add to cart button
   * @param {boolean} isSuccess - Success state
   * @private
   */
  #setSuccessState(isSuccess) {
    if (!this.refs.addButton) return;

    this.refs.addButton.classList.toggle('nms-product-card__add-button--success', isSuccess);

    if (isSuccess && this.refs.addButtonText) {
      this.refs.addButtonText.textContent = 'Added!';
    } else {
      this.#updateButtonState(); // Reset to normal state
    }
  }

  /**
   * Set error state on add to cart button
   * @param {boolean} isError - Error state
   * @private
   */
  #setErrorState(isError) {
    if (!this.refs.addButton) return;

    this.refs.addButton.classList.toggle('nms-product-card__add-button--error', isError);

    if (isError && this.refs.addButtonText) {
      // For now, just show generic "Try Again" - could use errorMessage in future
      this.refs.addButtonText.textContent = 'Try Again';
    } else {
      this.#updateButtonState(); // Reset to normal state
    }
  }

  /**
   * Format money using inherited method and remove .00 for whole numbers
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price
   * @private
   */
  #formatMoney(cents) {
    // Use inherited formatPrice method from NMSBaseClass
    const formattedPrice = this.formatPrice(cents);
    // Remove .00 from whole number prices to match Liquid template formatting
    return formattedPrice.replace('.00', '');
  }

  /**
   * Check if product has a size option
   * @returns {boolean}
   * @private
   */
  #hasSizeOption() {
    const sizeLabels = ['size', 'sizes', 'length', 'width', 'shoe size', 'clothing size', 'shirt size'];
    return this.productData.options.some(option => sizeLabels.includes(option.name.toLowerCase()));
  }

  /**
   * Handle size popup open
   * @param {Event} event - Click event from button (auto-bound)
   */
  handleSizePopupOpen(event) {
    if (event) {
      event.stopPropagation(); // Prevent bubbling
    }
    // Create size popup if it doesn't exist
    if (!this.sizePopup && window.NMSSizePopup) {
      this.sizePopup = new NMSSizePopup({
        productId: this.productId,
        productData: this.productData,
        triggerElement: this.refs.selectSizeButton,
        containerElement: document.body, // Append to body to escape modal stacking contexts
        onSelect: selection => this.#handleSizePopupSelection(selection),
        onClose: () => {
          // Hide overlay when popup closes
          const overlay = document.querySelector('.nms-overlay');
          if (overlay) {
            overlay.classList.remove('nms-overlay--size-popup-active');
          }
        },
      });
      this.sizePopup.init();
    }

    // Update product data in case it changed
    if (this.sizePopup) {
      this.sizePopup.updateProductData(this.productData);
      this.sizePopup.show();
    }

    // Show overlay
    const overlay = document.querySelector('.nms-overlay');
    if (overlay) {
      overlay.classList.add('nms-overlay--size-popup-active');
    }
  }

  /**
   * Handle size popup close
   */
  handleSizePopupClose() {
    if (this.sizePopup) {
      this.sizePopup.hide();
      // Note: Overlay is hidden via onClose callback
    }
  }

  /**
   * Handle size selection from popup
   * @param {Object} selection - Selection data from popup
   * @private
   */
  #handleSizePopupSelection(selection) {
    // Update selected options
    this.selectedOptions[`option${selection.optionPosition}`] = selection.size;

    // Find matching variant
    const matchingVariant = this.#findMatchingVariant(this.selectedOptions);

    if (matchingVariant && matchingVariant.available) {
      this.selectedVariant = matchingVariant;
      this.#updateVariantUI();

      // Trigger add to cart after popup closes
      this.isSubmitting = true;
      this.#setCartButtonLoading(true);
      setTimeout(() => {
        this.#addToCartWithSelectedVariant();
      }, 250);
    }
  }

  /**
   * Add to cart with currently selected variant
   * @private
   */
  #addToCartWithSelectedVariant() {
    if (!this.selectedVariant || !this.selectedVariant.available) {
      this.#setCartButtonLoading(false);
      this.isSubmitting = false;
      return;
    }

    // Update hidden variant ID input
    if (this.refs.variantId) {
      this.refs.variantId.value = this.selectedVariant.id;
    }

    // Programmatically submit the product form.  A hidden submit button's
    // .click() does not trigger submission, so use requestSubmit when
    // available and fall back to a synthetic submit event.
    const form = this.refs.addButton?.form;
    if (form) {
      if (form.requestSubmit) {
        form.requestSubmit(this.refs.addButton);
      } else {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    }
  }

  /**
   * Get the active cart button (visible select-size button or regular add button)
   * @returns {HTMLElement|null}
   * @private
   */
  #getActiveCartButton() {
    return this.refs.selectSizeButton || this.refs.addButton;
  }

  /**
   * Toggle loading state on the active cart button
   * @param {boolean} isLoading
   * @private
   */
  #setCartButtonLoading(isLoading) {
    const button = this.#getActiveCartButton();
    if (!button) return;

    if (isLoading) {
      button.classList.add('nms-button--loading');
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    } else {
      button.classList.remove('nms-button--loading');
      button.setAttribute('aria-busy', 'false');
      button.disabled = false;
    }
  }

  /**
   * Handle initialization errors gracefully
   * @param {Error} error - Initialization error
   * @private
   */
  #handleInitError(error) {
    // Use inherited error handling
    const message = this.handleError(error, 'Product card initialization failed');

    // Hide variant selection if it failed to initialize
    if (this.refs.variantsContainer) {
      this.refs.variantsContainer.style.display = 'none';
    }

    // Ensure basic functionality still works (direct to product page)
    const productLinks = this.element.querySelectorAll('a[href]');
    productLinks.forEach(link => {
      link.style.display = ''; // Ensure links are still visible
    });
  }

  /**
   * Handle color swatch click for product switching (auto-bound)
   * @param {Event} event - Click event from color swatch (auto-bound)
   * @param {string} productHandle - Product handle to switch to (from data-nms-click)
   */
  async handleColorSwatchClick(event, productHandle) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation(); // Prevent global color swatches from handling
    }

    const swatch = event.currentTarget;

    // Don't switch if already active
    if (swatch.classList.contains('nms-product-card__color-swatch--active')) {
      return;
    }

    // Don't switch if component doesn't have color data or API access
    if (!this.hasColorData || !this.storefrontAccessToken || !this.storeDomain) {
      console.warn('[NMS] Color switching not available - missing configuration', {
        hasColorData: this.hasColorData,
        hasToken: !!this.storefrontAccessToken,
        hasDomain: !!this.storeDomain,
      });
      return;
    }

    try {
      // Check if storefront client exists
      if (!window.neemans?.storefrontClient) {
        throw new Error('Storefront client not initialized');
      }

      this.#showColorSwitchingLoading(true);

      // Check cache first for repeat clicks (keep performance benefit)
      const cacheKey = `color-product:${productHandle}`;
      const cachedData = this.cache.get(cacheKey);
      let productData;

      if (cachedData && Date.now() - cachedData.timestamp < 300000) {
        // Use cached data for repeat clicks (5 minute TTL)
        productData = cachedData.data;
      } else {
        // Fetch from API and cache the result
        productData = await this.#fetchProductByHandle(productHandle);

        if (productData) {
          this.cache.set(cacheKey, {
            data: productData,
            timestamp: Date.now(),
          });

          // Preload product image for smooth switching
          this.#preloadProductImage(productData);
        }
      }

      if (productData) {
        await this.#updateProductData(productData);
        this.#updateActiveColorSwatch(productHandle);

        // Dispatch color switch success event
        document.dispatchEvent(
          new CustomEvent('nms:color-switched', {
            detail: {
              productId: this.productId,
              newProductHandle: productHandle,
              newProductData: productData,
              component: this,
            },
          })
        );
      }
    } catch (error) {
      console.error('[NMS] Color switching failed:', error);
      console.error('Debug info:', {
        errorMessage: error.message,
        hasStorefrontClient: !!window.neemans?.storefrontClient,
        hasAccessToken: !!this.storefrontAccessToken,
        productHandle: productHandle,
        stack: error.stack,
      });

      // Fallback to page navigation
      const fallbackUrl = swatch.dataset.productUrl || `/products/${productHandle}`;
      window.location.href = fallbackUrl;
    } finally {
      this.#showColorSwitchingLoading(false);
    }
  }

  /**
   * Fetch product data from Shopify Storefront API
   * @param {string} handle - Product handle
   * @returns {Promise<Object>} Product data
   * @private
   */
  async #fetchProductByHandle(handle) {
    // Ensure storefront client has access token and domain
    if (window.neemans.storefrontClient) {
      if (this.storefrontAccessToken) {
        window.neemans.storefrontClient.setAccessToken(this.storefrontAccessToken);
      }
      if (this.storeDomain) {
        window.neemans.storefrontClient.setStoreDomain(this.storeDomain);
      }
    }

    // Use shared storefront client
    return window.neemans.storefrontClient.fetchProductByHandle(handle);
  }

  /**
   * Update product data and UI with new product
   * @param {Object} newProductData - New product data
   * @private
   */
  async #updateProductData(newProductData) {
    // Update internal product data
    this.productData.id = newProductData.id;
    this.productData.handle = newProductData.handle;
    this.productData.title = newProductData.title;
    this.productData.available = newProductData.available;
    this.productData.variants = newProductData.variants;
    this.productData.options = newProductData.options;

    // Update product image
    if (newProductData.featured_media) {
      // Try multiple selectors to find the product image
      const productImage =
        this.refs.productImage ||
        this.element.querySelector('.nms-product-card__image') ||
        this.element.querySelector('[data-ref="productImage"]') ||
        this.element.querySelector('img');

      if (productImage) {
        console.log('[NMS] Updating product image from:', productImage.src, 'to:', newProductData.featured_media.url);

        // Show loading state without clearing image
        productImage.style.opacity = '0.5';
        productImage.style.filter = 'blur(2px)';

        // Get base URL without parameters
        const baseImageUrl = newProductData.featured_media.url.split('?')[0];

        // Generate new src and srcset with cache busting
        const timestamp = Date.now();
        const newSrc = `${baseImageUrl}?v=${timestamp}&width=800`;
        const newSrcset = [
          `${baseImageUrl}?v=${timestamp}&width=200 200w`,
          `${baseImageUrl}?v=${timestamp}&width=300 300w`,
          `${baseImageUrl}?v=${timestamp}&width=400 400w`,
          `${baseImageUrl}?v=${timestamp}&width=500 500w`,
          `${baseImageUrl}?v=${timestamp}&width=600 600w`,
          `${baseImageUrl}?v=${timestamp}&width=700 700w`,
          `${baseImageUrl}?v=${timestamp}&width=800 800w`,
        ].join(', ');

        // Create new image element to preload
        const newImage = new Image();
        newImage.onload = () => {
          // Once new image is loaded, update the visible image
          productImage.src = newSrc;
          productImage.srcset = newSrcset;
          productImage.alt = newProductData.featured_media.alt || newProductData.title;

          // Remove loading state
          productImage.style.opacity = '';
          productImage.style.filter = '';
        };

        newImage.onerror = () => {
          // If preload fails, update anyway but keep loading state briefly
          productImage.src = newSrc;
          productImage.srcset = newSrcset;
          productImage.alt = newProductData.featured_media.alt || newProductData.title;

          setTimeout(() => {
            productImage.style.opacity = '';
            productImage.style.filter = '';
          }, 500);
        };

        // Start preloading the new image
        newImage.src = newSrc;
      } else {
        console.warn('[NMS] Product image element not found for update. Available elements:', {
          productImageRef: !!this.refs.productImage,
          imageClass: !!this.element.querySelector('.nms-product-card__image'),
          imageDataRef: !!this.element.querySelector('[data-ref="productImage"]'),
          anyImage: !!this.element.querySelector('img'),
        });
      }
    }

    // Update product title and links
    console.log('[NMS] Updating product title...', {
      hasProductTitle: !!this.refs.productTitle,
      newTitle: newProductData.title,
      newHandle: newProductData.handle,
    });

    if (this.refs.productTitle) {
      const titleLink = this.refs.productTitle.querySelector('a');
      console.log('[NMS] Title link found:', !!titleLink);
      if (titleLink) {
        console.log('[NMS] Updating title from:', titleLink.textContent, 'to:', newProductData.title);
        titleLink.textContent = newProductData.title;
        titleLink.href = `/products/${newProductData.handle}`;
      }
    }

    // Update product image link
    const imageLink = this.element.querySelector('.nms-product-card__image-link');
    if (imageLink) {
      imageLink.href = `/products/${newProductData.handle}`;
    }

    if (this.refs.productLink) {
      this.refs.productLink.href = `/products/${newProductData.handle}`;
    }

    // Reset to first available variant
    this.selectedVariant = newProductData.variants.find(v => v.available) || newProductData.variants[0];

    if (this.selectedVariant) {
      this.selectedOptions = {
        option1: this.selectedVariant.option1,
        option2: this.selectedVariant.option2,
        option3: this.selectedVariant.option3,
      };
    }

    // Update variant selectors
    await this.#updateVariantSelectors(newProductData);

    // Update size popup if it exists
    if (this.hasSizePopup && this.sizePopup) {
      this.sizePopup.updateProductData(newProductData);
    }

    // Update UI
    console.log('[NMS] Updating variant UI with new product data...', {
      hasSelectedVariant: !!this.selectedVariant,
      selectedVariantPrice: this.selectedVariant?.price,
      selectedVariantCompareAt: this.selectedVariant?.compare_at_price,
    });
    this.#updateVariantUI();
  }

  /**
   * Update variant selector options with new product data
   * @param {Object} newProductData - New product data
   * @private
   */
  async #updateVariantSelectors(newProductData) {
    // Update dropdown selectors
    this.refs.optionSelects?.forEach((select, index) => {
      const optionPosition = index + 1;
      const option = newProductData.options.find(opt => opt.position === optionPosition);

      if (option) {
        // Clear existing options
        select.innerHTML = '';

        // Add new options
        option.values.forEach(value => {
          const optionElement = document.createElement('option');
          optionElement.value = value;
          optionElement.textContent = value;
          select.appendChild(optionElement);
        });

        // Select first option
        if (option.values.length > 0) {
          select.value = option.values[0];
        }
      }
    });

    // Update size boxes if present
    const sizeInputs = this.element.querySelectorAll('.nms-product-card__size-input');
    if (sizeInputs.length > 0) {
      // For size boxes, we need to rebuild them completely
      // This is a complex operation that would require template regeneration
      // For now, we'll just update availability
      this.#updateSizeAvailability();
    }
  }

  /**
   * Update active state of color swatches
   * @param {string} activeHandle - Handle of active product
   * @private
   */
  #updateActiveColorSwatch(activeHandle) {
    const colorSwatches = this.element.querySelectorAll('[data-ref="colorSwatch"]');

    colorSwatches.forEach(swatch => {
      const swatchHandle = swatch.dataset.productHandle;
      const isActive = swatchHandle === activeHandle;

      swatch.classList.toggle('nms-product-card__color-swatch--active', isActive);
      swatch.disabled = isActive;
    });
  }

  /**
   * Show/hide loading state during color switching
   * @param {boolean} isLoading - Loading state
   * @private
   */
  #showColorSwitchingLoading(isLoading) {
    if (isLoading) {
      // Use inherited loading methods
      if (this.refs.colorSwatches) {
        this.showLoading(this.refs.colorSwatches);
        this.refs.colorSwatches.style.pointerEvents = 'none';
      }

      if (this.refs.productImage) {
        this.refs.productImage.style.opacity = '0.6';
      }
    } else {
      // Use inherited loading methods
      if (this.refs.colorSwatches) {
        this.hideLoading(this.refs.colorSwatches);
        this.refs.colorSwatches.style.pointerEvents = '';
      }

      if (this.refs.productImage) {
        this.refs.productImage.style.opacity = '';
      }
    }
  }

  /**
   * Custom Carousel Navigation Methods
   */

  /**
   * Initialize custom swatch carousel if present
   * @private
   */
  #initSwatchCarousel() {
    // First, refresh refs in case carousel was added after initial scan
    this.#refreshCarouselRefs();

    if (this.refs.swatchContainer && this.refs.prevButton && this.refs.nextButton) {
      // Find the scrollable list
      const scrollList = this.refs.swatchContainer.querySelector('.nms-product-card__color-list--carousel');

      if (scrollList) {
        // Initialize button states based on current scroll position
        this.#updateCarouselButtonStates();

        // Add scroll event listener to update button states during scroll
        // Use passive listener for better performance since we don't preventDefault
        scrollList.addEventListener(
          'scroll',
          () => {
            this.#updateCarouselButtonStates();
          },
          { passive: true }
        );
      }
    }
  }

  /**
   * Calculate carousel scroll dimensions dynamically from DOM
   * @private
   */
  #calculateCarouselDimensions(scrollList) {
    if (!scrollList) {
      return { swatchWidth: 48, gap: 8, scrollAmount: 112 }; // Fallback values
    }

    const swatches = scrollList.querySelectorAll('.nms-product-card__color-swatch');
    if (swatches.length < 2) {
      return { swatchWidth: 48, gap: 8, scrollAmount: 112 }; // Fallback values
    }

    // Get actual swatch width from first swatch
    const firstSwatch = swatches[0];
    const swatchRect = firstSwatch.getBoundingClientRect();
    const swatchWidth = swatchRect.width;

    // Calculate gap by measuring distance between two swatches
    const secondSwatch = swatches[1];
    const secondSwatchRect = secondSwatch.getBoundingClientRect();
    const gap = secondSwatchRect.left - swatchRect.right;

    // Scroll by 2 swatches worth of distance
    const scrollAmount = (swatchWidth + gap) * 2;

    return { swatchWidth, gap, scrollAmount };
  }

  /**
   * Refresh refs specifically for carousel elements
   * @private
   */
  #refreshCarouselRefs() {
    if (!this.element) {
      return;
    }

    // Scan for carousel-specific refs within this component
    const carouselRefs = ['swatchContainer', 'prevButton', 'nextButton', 'swatchCarousel'];

    for (const refName of carouselRefs) {
      const element = this.element.querySelector(`[data-ref="${refName}"]`);
      if (element) {
        this.refs[refName] = element;
      }
    }
  }

  /**
   * Handle previous button click in custom carousel
   * @param {Event} event - Click event
   */
  handleSwatchNavPrev(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Refresh refs if missing (fallback for timing issues)
    if (!this.refs.swatchContainer) {
      this.#refreshCarouselRefs();
    }

    const scrollList = this.refs.swatchContainer?.querySelector('.nms-product-card__color-list--carousel');
    if (scrollList) {
      // Calculate scroll amount dynamically from actual DOM dimensions
      const { scrollAmount } = this.#calculateCarouselDimensions(scrollList);

      scrollList.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth',
      });

      // Update button states after smooth scroll animation completes
      setTimeout(() => this.#updateCarouselButtonStates(), 300);
    }
  }

  /**
   * Handle next button click in custom carousel
   * @param {Event} event - Click event
   */
  handleSwatchNavNext(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Refresh refs if missing (fallback for timing issues)
    if (!this.refs.swatchContainer) {
      this.#refreshCarouselRefs();
    }

    const scrollList = this.refs.swatchContainer?.querySelector('.nms-product-card__color-list--carousel');
    if (scrollList) {
      // Calculate scroll amount dynamically from actual DOM dimensions
      const { scrollAmount } = this.#calculateCarouselDimensions(scrollList);

      scrollList.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
      });

      // Update button states after smooth scroll animation completes
      setTimeout(() => this.#updateCarouselButtonStates(), 300);
    }
  }

  /**
   * Update carousel navigation button states based on scroll position
   * @private
   */
  #updateCarouselButtonStates() {
    if (!this.refs.prevButton || !this.refs.nextButton || !this.refs.swatchContainer) {
      return;
    }

    const scrollList = this.refs.swatchContainer.querySelector('.nms-product-card__color-list--carousel');
    if (!scrollList) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = scrollList;
    const threshold = 5; // Small threshold for floating point precision

    // At start if scroll is at or near beginning
    const atStart = scrollLeft <= threshold;

    // At end if scroll is at or near the end
    const atEnd = scrollLeft >= scrollWidth - clientWidth - threshold;

    // Update button states based on scroll position
    this.refs.prevButton.disabled = atStart;
    this.refs.nextButton.disabled = atEnd;
  }
}

// Initialize NMS Product Cards following standard pattern
function initNMSProductCard() {
  // Check for required base class
  if (!window.NMSBaseClass) {
    console.error('[NMS] NMSBaseClass is required for NMSProductCard');
    return;
  }

  const productCards = document.querySelectorAll('.nms-product-card');

  for (const card of productCards) {
    // Skip if already initialized
    if (!card.hasAttribute('data-initialized')) {
      try {
        const instance = new NMSProductCard(card);
        instance.init();
        card.setAttribute('data-initialized', 'true');
      } catch (error) {
        console.error('[NMS] Failed to initialize product card:', error);
      }
    }
  }
}

/**
 * Simple overlay click handler - closes size popup when overlay is clicked
 */
function initOverlayClickHandler() {
  const overlay = document.querySelector('.nms-overlay');
  if (!overlay) return;

  overlay.addEventListener('click', event => {
    event.preventDefault();
    event.stopPropagation();

    // Only handle clicks when size popup overlay is active (not mega menu overlay)
    if (!overlay.classList.contains('nms-overlay--size-popup-active')) {
      return;
    }

    const openPopup = document.querySelector('.nms-size-popup--show');
    if (openPopup) {
      const productCard = openPopup.closest('.nms-product-card');
      if (productCard) {
        const productId = productCard.dataset.productId;
        const instance = window.neemans?.productCard?.[productId];

        if (instance && typeof instance.handleSizePopupClose === 'function') {
          instance.handleSizePopupClose();
        }
      }
    }
  });
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initNMSProductCard();
    initOverlayClickHandler();
  });
} else {
  initNMSProductCard();
  initOverlayClickHandler();
}

// Export for manual initialization if needed
window.NMSProductCard = NMSProductCard;
window.initNMSProductCard = initNMSProductCard;
